import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { Badge } from '../components/badge'
import { Button, Card, ErrorNote } from '../components/ui'
import { ApiError } from '../lib/api'
import { customerName, listCustomers, type CustomerListItemDto } from '../lib/customers-api'
import { formatJalaliDateTime, formatMobile, toPersianDigits } from '../lib/fa'

const messageOf = (error: unknown): string =>
  error instanceof ApiError ? error.messageFa : 'مشکلی پیش آمد. دوباره تلاش کنید.'

/** Long enough to skip the keystrokes of a mobile number, short enough not to feel stuck. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * Customers, newest first. Search, filter and page live in the URL (`?q=&suspended=1&page=`), so a result can be
 * shared, reloaded, and returned to with the back button from a customer's page.
 */
export function CustomersPage() {
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const suspended = params.get('suspended') === '1'
  const page = Math.max(1, Number(params.get('page')) || 1)

  /** What is in the box; the URL catches up once typing pauses. */
  const [draft, setDraft] = useState(q)
  // Back/forward changes the URL under the box; our own update must not eat a trailing space.
  useEffect(() => setDraft((d) => (d.trim() === q ? d : q)), [q])

  useEffect(() => {
    const next = draft.trim()
    if (next === q) return
    const timer = setTimeout(() => {
      // A new search starts at page one, and replaces history so typing is not a trail of entries.
      setParams(
        { ...(next ? { q: next } : {}), ...(suspended ? { suspended: '1' } : {}) },
        { replace: true },
      )
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [draft, q, suspended, setParams])

  const toggleSuspended = (on: boolean) => {
    const next = new URLSearchParams(params)
    if (on) next.set('suspended', '1')
    else next.delete('suspended')
    next.delete('page')
    setParams(next)
  }

  const customers = useQuery({
    queryKey: ['customers', { q, suspended, page }],
    queryFn: () => listCustomers({ q, suspended, page }),
    placeholderData: keepPreviousData,
  })

  const goTo = (target: number) => {
    const next = new URLSearchParams(params)
    if (target > 1) next.set('page', String(target))
    else next.delete('page')
    setParams(next)
  }
  const pages = customers.data
    ? Math.max(1, Math.ceil(customers.data.total / customers.data.pageSize))
    : 1

  return (
    <div className="max-w-6xl">
      <h1 className="mb-1 text-2xl font-bold">مشتریان</h1>
      <p className="mb-6 text-sm text-muted">جست‌وجو با نام، شماره موبایل یا کد ملی.</p>

      <Card className="flex flex-wrap items-end gap-6">
        <label className="block w-full max-w-md">
          <span className="mb-1.5 block text-sm font-medium text-strong">جست‌وجو</span>
          <input
            type="search"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="مثلاً علی رضایی یا ۰۹۱۲…"
            className="h-10 w-full rounded-xl border border-line bg-card px-3 text-strong outline-none transition-colors placeholder:text-muted/60 focus:border-brand-500"
          />
        </label>
        <label className="flex h-10 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={suspended}
            onChange={(e) => toggleSuspended(e.target.checked)}
            className="size-4 accent-brand-600"
          />
          فقط مسدودها
        </label>
      </Card>

      <Card className="mt-6 overflow-x-auto p-0">
        {customers.isPending ? (
          <p className="p-5 text-sm text-muted">در حال بارگذاری…</p>
        ) : customers.isError ? (
          <div className="p-5">
            <ErrorNote>{messageOf(customers.error)}</ErrorNote>
          </div>
        ) : customers.data.items.length === 0 ? (
          <p className="p-5 text-sm text-muted">
            {q
              ? `مشتری‌ای با «${q}»${suspended ? ' در میان مسدودها' : ''} پیدا نشد.`
              : suspended
                ? 'هیچ مشتری‌ای مسدود نیست.'
                : 'هنوز مشتری‌ای ثبت‌نام نکرده است.'}
          </p>
        ) : (
          <table className={`w-full text-sm ${customers.isPlaceholderData ? 'opacity-60' : ''}`}>
            <thead className="border-b border-line text-right text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">نام</th>
                <th className="px-4 py-3 font-medium">موبایل</th>
                <th className="px-4 py-3 font-medium">کد ملی</th>
                <th className="px-4 py-3 font-medium">عضویت</th>
                <th className="px-4 py-3 font-medium">سفارش</th>
                <th className="px-4 py-3 font-medium">بیمه‌نامه</th>
              </tr>
            </thead>
            <tbody>
              {customers.data.items.map((customer) => (
                <CustomerRow key={customer.id} customer={customer} />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {customers.data && customers.data.total > 0 ? (
        <div className="mt-4 flex items-center gap-3 text-sm">
          <Button
            variant="secondary"
            disabled={page <= 1 || customers.isFetching}
            onClick={() => goTo(page - 1)}
          >
            قبلی
          </Button>
          <span className="text-muted">
            صفحه {toPersianDigits(page)} از {toPersianDigits(pages)} —{' '}
            {toPersianDigits(customers.data.total)} مشتری
          </span>
          <Button
            variant="secondary"
            disabled={page >= pages || customers.isFetching}
            onClick={() => goTo(page + 1)}
          >
            بعدی
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function CustomerRow({ customer }: { customer: CustomerListItemDto }) {
  const navigate = useNavigate()
  const location = useLocation()
  const name = customerName(customer)
  const href = `/customers/${customer.id}`
  const state = { from: `${location.pathname}${location.search}` }

  return (
    <tr
      className="cursor-pointer border-b border-line last:border-0 hover:bg-sunken"
      onClick={() => void navigate(href, { state })}
    >
      <td className="px-4 py-3">
        <Link
          to={href}
          state={state}
          className="font-medium hover:text-brand-600"
          onClick={(e) => e.stopPropagation()}
        >
          {name ?? <span className="text-muted">بدون نام</span>}
        </Link>
        {customer.isProfileComplete ? null : (
          <span className="ms-2 text-xs text-muted">(پروفایل ناقص)</span>
        )}
        {customer.suspendedAt ? (
          <span className="ms-2" title={customer.suspendedReason ?? undefined}>
            <Badge tone="bad">مسدود</Badge>
          </span>
        ) : null}
      </td>
      <td className="px-4 py-3" dir="ltr" style={{ textAlign: 'right' }}>
        {formatMobile(customer.mobile)}
      </td>
      <td className="px-4 py-3">
        {customer.nationalCode ? toPersianDigits(customer.nationalCode) : '—'}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-muted">
        {formatJalaliDateTime(customer.createdAt)}
      </td>
      <td className="px-4 py-3">{toPersianDigits(customer.orderCount)}</td>
      <td className="px-4 py-3">{toPersianDigits(customer.policyCount)}</td>
    </tr>
  )
}
